import { useState, useRef, useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { unstable_batchedUpdates } from 'react-dom';
import {
  useEventCallback,
  FormHandle,
  SnackbarContext,
  SnackbarVariant,
} from '@eas/common-web';
import { LoginData } from '../../../common/models';
import { useLocation } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export function useLoginOperatorExternal() {
  const intl = useIntl();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showSnackbar } = useContext(SnackbarContext);
  const [loading, setLoading] = useState(false);
  const formRef = useRef<FormHandle<LoginData>>(null);
  const hiddenFormRef = useRef<HTMLFormElement>(null);
  const hiddenUsernameRef = useRef<HTMLInputElement>(null);
  const hiddenPasswordRef = useRef<HTMLInputElement>(null);
  const hiddenCaptchaRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (location.search === '?error') {
      showSnackbar('Chyba přihlášení', SnackbarVariant.ERROR);
    }
  });

  const handleSubmit = useEventCallback(async (data: LoginData) => {
    try {
      setLoading(true);

      const captcha = await executeRecaptcha!('operatorExternalLogin');

      hiddenUsernameRef.current!.value = data.username;
      hiddenPasswordRef.current!.value = data.password;
      hiddenCaptchaRef.current!.value = captcha;

      hiddenFormRef.current?.submit();

      unstable_batchedUpdates(() => {
        setLoading(false);
      });
    } catch (err) {
      setLoading(false);

      if (err.name !== 'AbortError') {
        const message = intl.formatMessage(
          {
            id: 'KS_P_LOGIN_EXO_MSG_ERROR',
            defaultMessage: 'Chyba přihlášení: {detail}',
          },
          { detail: err.message }
        );

        showSnackbar(message, SnackbarVariant.ERROR);

        throw err;
      }

      return undefined;
    }
  });

  return {
    loading,
    handleSubmit,
    hiddenFormRef,
    hiddenUsernameRef,
    hiddenPasswordRef,
    hiddenCaptchaRef,
    formRef,
  };
}
